import { sendToApi } from 'api/client'
import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery, UseQueryOptions } from 'react-query'
import { Response } from 'request-dot-js'
import { getNextPageParam } from 'utils/getNextPageParam'
import { FilesView, SortingDirection } from '../../../api/clients/FilesViewsClient'
import { defaultLongCacheTime } from '../../../queryClient'
import { FilterType } from 'screens/RecentDocsContainer/Types'
import { AlmanacFile } from 'types'

export interface Options {
  workspaceId: number
  location: string
  query: {
    items: number
    current_schema_id?: number
    sorting_property_id?: number | null
    sorting_direction?: SortingDirection
    accessible_as_template?: boolean
    skip_projects?: boolean
    skip_media_files?: boolean
    load_children_preview_urls?: boolean
    file_types?: string
    type?: FilterType
    filters?: string
    skip_files?: boolean
  }
  disableSchema?: boolean
  skipArticles?: boolean
  skipPinned?: boolean
  files_view_project_id?: string
}

export interface SchemaOptions {
  workspaceId: number
  location: string
  query: {
    current_schema_id?: number
  }
}

export const fetchFiles = <FileType = AlmanacFile>({
  workspaceId,
  location,
  query,
  disableSchema = false,
  skipArticles = false,
  skipPinned = false,
  files_view_project_id = undefined,
}: Options): (({ pageParam }: any) => Promise<Response<FilesView<FileType>>>) => {
  return ({ pageParam = 1 }) => {
    const {
      current_schema_id,
      accessible_as_template,
      skip_projects,
      skip_media_files,
      load_children_preview_urls,
      file_types,
      skip_files,
      ...rest
    } = query

    let params: any = {
      page: pageParam,
      ...rest,
      disableSchema,
      skip_articles: skipArticles,
      skip_pinned: skipPinned,
    }

    if (current_schema_id) {
      params.current_schema_id = current_schema_id
    }

    if (accessible_as_template) {
      params.accessible_as_template = accessible_as_template
    }

    if (skip_projects) {
      params.skip_projects = skip_projects
    }

    if (skip_media_files) {
      params.skip_media_files = skip_media_files
    }

    if (load_children_preview_urls) {
      params.load_children_preview_urls = load_children_preview_urls
    }

    if (files_view_project_id) {
      params.files_view_project_id = files_view_project_id
    }

    if (file_types) {
      params.file_types = file_types
    }

    if (skip_files) {
      params.skip_files = skip_files
    }

    return sendToApi<FilesView<FileType>>(`/w/${workspaceId}/files_views/${location}`, {
      method: 'GET',
      params,
    })
  }
}

export const useFiles = <FileType = AlmanacFile>(
  options: Options,
  reactQueryOptions?: UseInfiniteQueryOptions<Response<FilesView<FileType>>>,
) => {
  return useInfiniteQuery<Response<FilesView<FileType>>>(
    [
      'workspace',
      options.workspaceId,
      'files',
      options.location,
      JSON.stringify(options.query),
      options.files_view_project_id,
    ],
    fetchFiles<FileType>(options),
    {
      cacheTime: defaultLongCacheTime,
      refetchOnWindowFocus: false,
      getNextPageParam,
      ...reactQueryOptions,
    },
  )
}

export const useFilesSchema = (
  options: SchemaOptions,
  reactQueryOptions?: UseQueryOptions<Response<Omit<FilesView, 'files'>>>,
) => {
  return useQuery<Response<Omit<FilesView, 'files'>>>(
    ['workspace', options.workspaceId, 'schema', options.location, JSON.stringify(options.query)],
    () => {
      return sendToApi<FilesView>(`/w/${options.workspaceId}/files_views/${options.location}/schema`, {
        method: 'GET',
        params: { current_schema_id: options.query.current_schema_id },
      })
    },
    reactQueryOptions,
  )
}
